import { DOCUMENT_KINDS, safeFileName } from './loadDocs';

/**
 * Carrier, truck and driver documents (change spec documents, design 3). The kinds each owner holds (mirror
 * of `app.document_kind_owner`, 0126), the money kinds only admin and accounting read and write, the storage
 * path `fleet_document_add` checks, and the four statuses `document_status` computes.
 */

export const OWNER_KINDS = ['carrier', 'truck', 'driver'] as const;
export type OwnerKind = (typeof OWNER_KINDS)[number];

export function isOwnerKind(v: unknown): v is OwnerKind {
  return typeof v === 'string' && (OWNER_KINDS as readonly string[]).includes(v);
}

/**
 * audit-b-docs (M7, 0312): the owners a requirement can name. 'load' is NOT a fleet owner (no storage path, no
 * checklist row, no Documentation tab): only the Requirements dialog lists it.
 */
export const REQUIREMENT_OWNER_KINDS = [...OWNER_KINDS, 'load'] as const;
export type RequirementOwnerKind = (typeof REQUIREMENT_OWNER_KINDS)[number];

/** The kinds a load requirement may name: the load document kinds without `other` (the server refuses it). */
export const LOAD_REQUIREMENT_KINDS = DOCUMENT_KINDS.filter((k) => k !== 'other');

/** The kinds each owner holds, in the order the checklist lists them when no requirement orders them. */
export const KINDS_BY_OWNER = {
  carrier: [
    'mc_authority',
    'dot_authority',
    'insurance',
    'w9',
    'dispatch_agreement',
    'limited_poa',
    'company_profile',
    'truck_operation_form',
    'carrier_references',
    'void_check',
    'noa',
    'factoring',
    'zelle_info',
    'other',
  ],
  truck: ['registration', 'annual_inspection', 'cab_card', 'truck_photo', 'vin_photo', 'permit', 'maintenance_record', 'other'],
  driver: ['cdl', 'medical_card', 'other'],
} as const satisfies Record<OwnerKind, readonly string[]>;

export type FleetDocumentKind = (typeof KINDS_BY_OWNER)[OwnerKind][number];

/** Bank and contract papers: admin and accounting only (documents_sel, 0126). */
export const MONEY_KINDS: readonly string[] = ['dispatch_agreement', 'void_check', 'zelle_info'];

export function isMoneyKind(kind: string): boolean {
  return MONEY_KINDS.includes(kind);
}

/** Several live copies make sense (three references, several photos); every other kind has one current copy. */
export const MULTI_COPY_KINDS: readonly string[] = ['carrier_references', 'truck_photo', 'maintenance_record', 'other'];

export const DOC_STATUSES = ['valid', 'expiring', 'expired', 'missing'] as const;
export type DocStatus = (typeof DOC_STATUSES)[number];

/** `{agency}/{owner_kind}/{owner}/{kind}/{uuid}-{name}`: the five segments `fleet_document_add` checks. */
export function fleetStoragePath(agencyId: string, ownerKind: OwnerKind, ownerId: string, kind: string, uuid: string, name: string): string {
  return `${agencyId}/${ownerKind}/${ownerId}/${kind}/${uuid}-${safeFileName(name)}`;
}

export type UrgencyTone = 'critical' | 'warning' | 'ok' | 'missing';

/**
 * The chip tone of a checklist line (design/tokens.json `urgency.*`): expired or a week or less left is
 * critical, a month or less a warning, valid is ok, missing is slate. The words and the day count are always
 * rendered next to it: the tone reinforces, it never carries the message alone.
 */
export function urgencyTone(status: DocStatus, daysLeft: number | null): UrgencyTone {
  if (status === 'missing') return 'missing';
  if (status === 'expired') return 'critical';
  if (daysLeft === null) return 'ok';
  if (daysLeft <= 7) return 'critical';
  if (daysLeft <= 30) return 'warning';
  return 'ok';
}

export const TONE_CLASSES: Record<UrgencyTone, string> = {
  critical: 'bg-urgency-critical-bg text-urgency-critical-fg',
  warning: 'bg-urgency-warning-bg text-urgency-warning-fg',
  ok: 'bg-urgency-ok-bg text-urgency-ok-fg',
  missing: 'bg-urgency-missing-bg text-urgency-missing-fg',
};

/** One line of `document_status`, camelCased. */
export interface ChecklistRow {
  ownerKind: OwnerKind;
  ownerId: string;
  ownerName: string;
  ownerStatus: string;
  carrierId: string | null;
  kind: string;
  isRequired: boolean;
  warnDays: number;
  status: DocStatus;
  documentId: string | null;
  fileName: string | null;
  storagePath: string | null;
  issuedOn: string | null;
  expiresOn: string | null;
  daysLeft: number | null;
  copies: number;
}

/** A PostgREST row of `document_status` -> ChecklistRow (unknown statuses read as missing, never crash). */
export function toChecklistRow(r: Record<string, unknown>): ChecklistRow {
  const status = String(r.status ?? 'missing');
  const days = r.days_left;
  return {
    ownerKind: (isOwnerKind(r.owner_kind) ? r.owner_kind : 'carrier') as OwnerKind,
    ownerId: String(r.owner_id ?? ''),
    ownerName: String(r.owner_name ?? ''),
    ownerStatus: String(r.owner_status ?? 'active'),
    carrierId: (r.carrier_id as string | null) ?? null,
    kind: String(r.kind ?? 'other'),
    isRequired: Boolean(r.is_required),
    warnDays: Number(r.warn_days ?? 30),
    status: ((DOC_STATUSES as readonly string[]).includes(status) ? status : 'missing') as DocStatus,
    documentId: (r.document_id as string | null) ?? null,
    fileName: (r.file_name as string | null) ?? null,
    storagePath: (r.storage_path as string | null) ?? null,
    issuedOn: (r.issued_on as string | null) ?? null,
    expiresOn: (r.expires_on as string | null) ?? null,
    daysLeft: days === null || days === undefined ? null : Number(days),
    copies: Number(r.copies ?? 0),
  };
}

/** Per owner: the checklist and its counts. `attention` = required missing + expiring + expired. */
export interface OwnerSummary {
  ownerKind: OwnerKind;
  ownerId: string;
  ownerName: string;
  ownerStatus: string;
  carrierId: string | null;
  rows: ChecklistRow[];
  requiredTotal: number;
  requiredOnFile: number;
  missing: number;
  expiring: number;
  expired: number;
  attention: number;
}

export function summarize(rows: readonly ChecklistRow[]): OwnerSummary[] {
  const byOwner = new Map<string, OwnerSummary>();
  for (const r of rows) {
    const key = `${r.ownerKind}:${r.ownerId}`;
    let s = byOwner.get(key);
    if (!s) {
      s = {
        ownerKind: r.ownerKind,
        ownerId: r.ownerId,
        ownerName: r.ownerName,
        ownerStatus: r.ownerStatus,
        carrierId: r.carrierId,
        rows: [],
        requiredTotal: 0,
        requiredOnFile: 0,
        missing: 0,
        expiring: 0,
        expired: 0,
        attention: 0,
      };
      byOwner.set(key, s);
    }
    s.rows.push(r);
    if (r.isRequired) {
      s.requiredTotal += 1;
      if (r.status !== 'missing') s.requiredOnFile += 1;
      if (r.status === 'missing') s.missing += 1;
    }
    if (r.status === 'expiring') s.expiring += 1;
    if (r.status === 'expired') s.expired += 1;
  }
  for (const s of byOwner.values()) s.attention = s.missing + s.expiring + s.expired;
  return [...byOwner.values()];
}

export type AttentionFilter = 'all' | 'attention' | 'missing' | 'expiring' | 'expired' | 'complete';
export const ATTENTION_FILTERS: readonly AttentionFilter[] = ['all', 'attention', 'missing', 'expiring', 'expired', 'complete'];

export function matchesFilter(s: OwnerSummary, f: AttentionFilter): boolean {
  switch (f) {
    case 'all':
      return true;
    case 'attention':
      return s.attention > 0;
    case 'missing':
      return s.missing > 0;
    case 'expiring':
      return s.expiring > 0;
    case 'expired':
      return s.expired > 0;
    case 'complete':
      return s.attention === 0;
  }
}

/**
 * The line a machine `document_expiry` task stores (0128): `{owner_kind} {owner}: {kind} {expires|expired} on
 * {YYYY-MM-DD}`. The owner name may itself hold ': ', so the kind is taken from the LAST separator.
 */
const EXPIRY_DETAIL = /^(carrier|truck|driver) (.+): ([a-z_]+) (expires|expired) on (\d{4}-\d{2}-\d{2})$/;

/** An expiry date WITH its year ("Oct 4, 2027"): documents run for years, so the short calendar format
 *  (month and day) would hide which year. Formatted in UTC so the stored day is the day shown. */
export function expiryDate(ymd: string | null, locale = 'en-US'): string {
  if (!ymd) return '';
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d);
}

export interface ExpiryDetail {
  ownerKind: OwnerKind;
  ownerName: string;
  kind: string;
  expired: boolean;
  date: string;
}

export function parseExpiryDetail(detail: string | null | undefined): ExpiryDetail | null {
  const m = detail ? EXPIRY_DETAIL.exec(detail) : null;
  if (!m) return null;
  return { ownerKind: m[1] as OwnerKind, ownerName: m[2], kind: m[3], expired: m[4] === 'expired', date: m[5] };
}
